<template>
  <v-app>
    <v-container fluid>
      <v-row v-if="$insertAccess($route.path)">
        <v-col cols="12" md="3">
          <v-card variant="outlined">
            <v-card-title class="bg-blue-grey-lighten-4 text-subtitle-1">
              Material Issue Information
            </v-card-title>
            <v-card-text class="pt-4">
              <v-autocomplete
                v-model="form.requisition_master_id"
                :items="eligibleRequisitions"
                :item-title="requisitionTitle"
                item-value="id"
                label="Approved Requisition *"
                variant="outlined"
                density="compact"
                clearable
                :disabled="Boolean(editId)"
                :menu-props="{ contentClass: 'my-custom-dropdown' }"
                @update:model-value="loadRequisition"
              />

              <v-autocomplete
                v-model="form.warehouse_id"
                :items="warehouses"
                item-title="name"
                item-value="id"
                label="Issue Warehouse *"
                variant="outlined"
                density="compact"
                clearable
                :menu-props="{ contentClass: 'my-custom-dropdown' }"
                @update:model-value="clearLotResults"
              />

              <v-autocomplete
                v-model="form.department_id"
                :items="departments"
                item-title="name"
                item-value="id"
                label="Department (optional)"
                variant="outlined"
                density="compact"
                clearable
                :menu-props="{ contentClass: 'my-custom-dropdown' }"
              />

              <v-date-input
                v-model="form.requisition_delivery_date"
                label="Issue Date *"
                input-format="dd/mm/yyyy"
                prepend-icon=""
                prepend-inner-icon="mdi-calendar"
                variant="outlined"
                density="compact"
                @update:model-value="clearLotResults"
              />

              <v-textarea
                v-model="form.remarks"
                label="Remarks"
                variant="outlined"
                density="compact"
                rows="3"
              />

              <v-alert type="info" variant="tonal" density="compact" class="mb-3">
                Standard Purity comes from Product Master. Purchase Purity and stock come from the selected Inventory lot.
              </v-alert>

              <v-btn
                block
                color="primary"
                prepend-icon="mdi-flask-outline"
                :loading="loadingAllLots"
                :disabled="!canLoadLots || cart.length === 0"
                @click="loadAllLots"
              >
                Load Lots &amp; Apply FEFO
              </v-btn>
            </v-card-text>
          </v-card>
        </v-col>

        <v-col cols="12" md="9">
          <v-card variant="outlined">
            <v-card-title class="d-flex flex-wrap align-center ga-2 bg-blue-grey-lighten-4 text-subtitle-1">
              <span>Production Material Issue — Lot Allocation</span>
              <v-spacer />
              <v-chip size="small" color="primary" variant="tonal">
                {{ cart.length }} requirement(s)
              </v-chip>
            </v-card-title>

            <v-card-text class="pt-4">
              <v-alert
                v-if="!form.requisition_master_id"
                type="info"
                variant="tonal"
              >
                Select an approved Production requisition to load raw material and packaging requirements.
              </v-alert>

              <v-alert
                v-else-if="cart.length === 0"
                type="warning"
                variant="tonal"
              >
                No remaining material requirement was found for this requisition.
              </v-alert>

              <div v-else class="d-flex flex-column ga-5">
                <section
                  v-for="group in materialGroups"
                  :key="group.key"
                  class="material-group"
                  :class="group.key === 'packaging' ? 'material-group--packaging' : 'material-group--raw'"
                >
                  <div class="material-group-header d-flex flex-wrap align-center ga-2 px-3 py-3">
                    <v-icon :icon="group.key === 'packaging' ? 'mdi-package-variant-closed' : 'mdi-flask-outline'" size="small" />
                    <div>
                      <div class="text-subtitle-1 font-weight-bold">{{ group.title }}</div>
                      <div class="text-caption text-medium-emphasis">{{ group.description }}</div>
                    </div>
                    <v-spacer />
                    <v-chip size="small" variant="tonal" :color="group.key === 'packaging' ? 'indigo' : 'teal'">
                      {{ group.lines.length }} item(s)
                    </v-chip>
                  </div>

                  <div v-if="group.lines.length === 0" class="px-3 py-4 text-body-2 text-medium-emphasis">
                    No {{ group.key === 'packaging' ? 'packaging' : 'raw' }} material requirements in this requisition.
                  </div>
                  <v-expansion-panels v-else v-model="openPanels[group.key]" multiple class="material-group-panels">
                    <v-expansion-panel
                      v-for="line in group.lines"
                      :key="line.requisition_raw_detail_id"
                      :value="line.requisition_raw_detail_id"
                    >
                      <v-expansion-panel-title>
                        <div class="d-flex flex-wrap align-center ga-2 w-100 pr-3">
                          <strong>{{ line.product_code }} - {{ line.product_name }}</strong>
                          <v-chip size="x-small" :color="line.item_type === 'packaging' ? 'indigo' : 'teal'">
                            {{ labelize(line.item_type) }}
                          </v-chip>
                          <v-chip size="x-small" :color="line.calculation_method === 'balancer' ? 'orange' : 'blue'">
                            {{ labelize(line.calculation_method) }}
                          </v-chip>
                          <v-spacer />
                          <span class="text-caption">
                            Need active: <strong>{{ number(line.remaining_active_quantity, 6) }}</strong>
                            {{ line.unit_name }} · Allocated active:
                            <strong :class="allocationClass(line)">{{ number(lineActive(line), 6) }}</strong>
                          </span>
                        </div>
                      </v-expansion-panel-title>

                      <v-expansion-panel-text>
                        <v-row dense class="mb-2">
                          <v-col cols="6" sm="3">
                            <div class="metric-label">{{ line.item_type === 'packaging' ? 'Allocation Basis' : 'Standard Purity' }}</div>
                            <div class="metric-value">{{ line.item_type === 'packaging' ? 'Quantity (No Purity)' : `${number(line.standard_purity, 4)}%` }}</div>
                          </v-col>
                          <v-col cols="6" sm="3">
                            <div class="metric-label">Physical Required</div>
                            <div class="metric-value">{{ number(line.total_required_quantity, 4) }} {{ line.unit_name }}</div>
                          </v-col>
                          <v-col cols="6" sm="3">
                            <div class="metric-label">Already Issued Active</div>
                            <div class="metric-value">{{ number(line.issued_active_quantity, 6) }}</div>
                          </v-col>
                          <v-col cols="6" sm="3">
                            <div class="metric-label">Allocation Cost</div>
                            <div class="metric-value">{{ number(lineAmount(line), 2) }}</div>
                          </v-col>
                        </v-row>

                        <v-alert
                          v-if="line.calculation_method === 'balancer'"
                          type="warning"
                          density="compact"
                          variant="tonal"
                          class="mb-2"
                        >
                          Salt/other balancer quantity is calculated from the actual fixed-material lot quantities. FEFO allocation refreshes after fixed lots change; the final amount is checked again on save.
                        </v-alert>

                        <v-alert
                          v-if="line.message"
                          :type="line.lots.length ? 'info' : 'warning'"
                          density="compact"
                          variant="tonal"
                          class="mb-2"
                        >
                          {{ line.message }}
                        </v-alert>

                        <div class="d-flex flex-wrap ga-2 mb-2">
                          <v-btn
                            size="small"
                            color="primary"
                            variant="tonal"
                            prepend-icon="mdi-refresh"
                            :loading="line.loading"
                            :disabled="!canLoadLots"
                            @click="loadLots(line, true)"
                          >
                            Refresh &amp; FEFO
                          </v-btn>
                          <v-btn
                            size="small"
                            variant="tonal"
                            prepend-icon="mdi-eraser"
                            :disabled="line.lots.length === 0"
                            @click="clearLine(line)"
                          >
                            Clear Allocation
                          </v-btn>
                          <v-btn
                            size="small"
                            color="error"
                            variant="text"
                            prepend-icon="mdi-delete-outline"
                            @click="removeLine(line)"
                          >
                            Skip Line
                          </v-btn>
                        </div>

                        <v-table density="compact" class="lot-table">
                          <thead>
                            <tr>
                              <th>Lot / Batch</th>
                              <th class="text-center">Purchase Purity</th>
                              <th class="text-center">Expiry</th>
                              <th class="text-end">Available</th>
                              <th class="text-end">Available Active</th>
                              <th class="text-end">Unit Cost</th>
                              <th style="width: 145px">Allocate Qty</th>
                              <th class="text-end">Active Contribution</th>
                            </tr>
                          </thead>
                          <tbody>
                            <tr v-for="lot in line.lots" :key="lot.inventory_master_id">
                              <td>
                                <div class="font-weight-medium">{{ lot.lot_no || '-' }}</div>
                                <div class="text-caption text-medium-emphasis">{{ lot.batch_no || '-' }}</div>
                              </td>
                              <td class="text-center">{{ line.item_type === 'packaging' ? 'N/A' : `${number(lot.purchase_purity, 4)}%` }}</td>
                              <td class="text-center">{{ lot.expiry_date || '-' }}</td>
                              <td class="text-end">{{ number(lot.available_quantity, 4) }}</td>
                              <td class="text-end">{{ number(lot.available_active_quantity, 6) }}</td>
                              <td class="text-end">{{ number(lot.unit_cost, 6) }}</td>
                              <td>
                                <v-text-field
                                  v-model.number="lot.quantity"
                                  type="number"
                                  min="0"
                                  :max="lot.available_quantity"
                                  step="0.0001"
                                  density="compact"
                                  variant="outlined"
                                  hide-details
                                  @update:model-value="onLotQuantityChange(line, lot)"
                                />
                              </td>
                              <td class="text-end font-weight-medium">
                                {{ number(lotActive(line, lot), 6) }}
                              </td>
                            </tr>
                            <tr v-if="!line.loading && line.lots.length === 0">
                              <td colspan="8" class="text-center text-medium-emphasis py-5">
                                Select warehouse/date and load available lots.
                              </td>
                            </tr>
                          </tbody>
                          <tfoot v-if="line.lots.length">
                            <tr class="font-weight-bold">
                              <td colspan="6" class="text-end">Selected total</td>
                              <td>{{ number(linePhysical(line), 4) }}</td>
                              <td class="text-end">{{ number(lineActive(line), 6) }}</td>
                            </tr>
                          </tfoot>
                        </v-table>
                      </v-expansion-panel-text>
                    </v-expansion-panel>
                  </v-expansion-panels>
                </section>
              </div>
            </v-card-text>

            <v-divider />
            <v-card-actions class="justify-end pa-4">
              <v-btn variant="tonal" :disabled="saving" @click="submit(0)">Save Draft</v-btn>
              <v-btn color="primary" :loading="saving" @click="submit(1)">Submit for Approval</v-btn>
            </v-card-actions>
          </v-card>
        </v-col>
      </v-row>
    </v-container>
  </v-app>
</template>

<script setup>
import { computed, onMounted, reactive, ref } from "vue";
import { useRoute, useRouter } from "vue-router";
import { useStore } from "vuex";
import { toast } from "vue3-toastify";
import formattedDate from "@/plugins/dateFormatter";

const store = useStore();
const route = useRoute();
const router = useRouter();

const editId = route.query.id || null;
const cart = ref([]);
// The display groups share the same source cart and save payload.
// Only expansion state is split so RM and PM can be opened independently.
const openPanels = reactive({ raw_material: [], packaging: [] });
const saving = ref(false);
const loadingAllLots = ref(false);
let balancerRefreshTimer = null;

const form = reactive({
  requisition_master_id: null,
  warehouse_id: null,
  department_id: null,
  requisition_delivery_date: new Date(),
  remarks: "",
});

const warehouses = computed(() => store.getters.getWarehouseList || []);
const departments = computed(() => store.getters.getHrmDepartmentList || []);
const requisitions = computed(() => store.getters.getRawMetarialRequisitionList || []);
const eligibleWorkflows = [
  "approved",
  "materials_partially_issued",
  "materials_issued",
  "materials_partially_received",
  "materials_received",
  "production_in_progress",
];

const eligibleRequisitions = computed(() => requisitions.value.filter((item) => {
  const approved = String(item.approval_status || "").toLowerCase() === "approved";
  return approved && eligibleWorkflows.includes(item.workflow_status || "approved");
}));

const canLoadLots = computed(() => Boolean(
  form.warehouse_id && form.requisition_delivery_date && form.requisition_master_id
));

// Presentation-only grouping: no changes to the requisition items, FEFO or accounting.
const materialGroups = computed(() => [
  {
    key: "raw_material",
    title: "Raw Materials (RM)",
    description: "Purity-adjusted raw materials, including the batch balancer",
    lines: cart.value.filter((line) => line.item_type !== "packaging"),
  },
  {
    key: "packaging",
    title: "Packaging Materials (PM)",
    description: "Quantity-based packaging issue; cost remains in the production valuation",
    lines: cart.value.filter((line) => line.item_type === "packaging"),
  },
]);

function expandMaterialGroups() {
  openPanels.raw_material = materialGroups.value[0].lines.map((line) => line.requisition_raw_detail_id);
  openPanels.packaging = materialGroups.value[1].lines.map((line) => line.requisition_raw_detail_id);
}

function requisitionTitle(item) {
  const order = item.sale_order?.code ? ` · ${item.sale_order.code}` : "";
  return `${item.code}${order} · ${labelize(item.workflow_status || "approved")}`;
}

function labelize(value) {
  return String(value || "-").replaceAll("_", " ").replace(/\b\w/g, (letter) => letter.toUpperCase());
}

function number(value, digits = 4) {
  return Number(value || 0).toLocaleString(undefined, {
    minimumFractionDigits: 0,
    maximumFractionDigits: digits,
  });
}

function mapRequirement(requirement, detail) {
  const remaining = Math.max(
    0,
    Number(requirement.active_required_quantity || 0) - Number(requirement.issued_active_quantity || 0)
  );
  return {
    requisition_raw_detail_id: requirement.id,
    production_requisition_detail_id: detail.id,
    product_id: requirement.raw_product_id || requirement.raw_product?.id,
    template_id: requirement.template_id || detail.template_id || null,
    product_code: requirement.raw_product?.code || "",
    product_name: requirement.raw_product?.name || "",
    unit_name: requirement.raw_product?.unit?.name || "",
    item_type: requirement.item_type || "raw_material",
    calculation_method: requirement.calculation_method || "fixed",
    standard_purity: Number(requirement.standard_purity ?? requirement.raw_product?.standard_purity ?? 0),
    total_required_quantity: Number(requirement.total_required_quantity || 0),
    active_required_quantity: Number(requirement.active_required_quantity || 0),
    issued_active_quantity: Number(requirement.issued_active_quantity || 0),
    remaining_active_quantity: remaining,
    lots: [],
    loading: false,
    message: "",
  };
}

async function loadRequisition(id) {
  if (balancerRefreshTimer) clearTimeout(balancerRefreshTimer);
  cart.value = [];
  openPanels.raw_material = [];
  openPanels.packaging = [];
  if (!id) return;

  try {
    const response = await store.dispatch("getRawMetarialRequisitionById", id);
    const data = response?.data || response;
    const lines = [];
    (data?.details || []).forEach((detail) => {
      (detail.raw_product_details || []).forEach((requirement) => {
        const line = mapRequirement(requirement, detail);
        if (line.remaining_active_quantity > 0.000001 || line.calculation_method === "balancer") {
          lines.push(line);
        }
      });
    });
    cart.value = lines;
    expandMaterialGroups();
  } catch (error) {
    toast.error(error?.response?.data?.message || "Unable to load Production requisition.");
  }
}

function clearLotResults() {
  cart.value.forEach((line) => {
    line.lots = [];
    line.message = "";
  });
}

function normalizeLot(lot, quantity = 0) {
  return {
    inventory_master_id: lot.inventory_master_id || lot.id,
    lot_no: lot.lot_no || "",
    batch_no: lot.batch_no || "",
    manufacturing_date: lot.manufacturing_date || null,
    expiry_date: lot.expiry_date || null,
    standard_purity: Number(lot.standard_purity || 0),
    purchase_purity: Number(lot.purchase_purity || 0),
    available_quantity: Number(lot.available_quantity ?? lot.quantity ?? 0),
    available_active_quantity: Number(lot.available_active_quantity || 0),
    unit_cost: Number(lot.unit_cost || 0),
    quantity: Number(quantity || 0),
  };
}

async function loadLots(line, applyFefo = true) {
  if (!canLoadLots.value) {
    toast.warning("Select requisition, issue warehouse and issue date first.");
    return;
  }

  line.loading = true;
  try {
    const fixedLineTotals = line.calculation_method === "balancer"
      ? JSON.stringify(cart.value
          .filter((candidate) => candidate.production_requisition_detail_id === line.production_requisition_detail_id
            && candidate.item_type !== "packaging" && candidate.calculation_method !== "balancer")
          .map((candidate) => ({
            requisition_raw_detail_id: candidate.requisition_raw_detail_id,
            physical_quantity: linePhysical(candidate),
            active_quantity: lineActive(candidate),
          })))
      : null;
    const response = await store.dispatch("getProductionRequisitionLots", {
      rawDetailId: line.requisition_raw_detail_id,
      warehouse_id: form.warehouse_id,
      issue_date: formattedDate(form.requisition_delivery_date),
      fixed_line_totals: fixedLineTotals,
    });
    const data = response?.data || response;
    const oldQuantities = new Map(line.lots.map((lot) => [Number(lot.inventory_master_id), Number(lot.quantity || 0)]));
    const recommendations = new Map(
      (data?.recommended_allocations || []).map((item) => [Number(item.inventory_master_id), Number(item.quantity || 0)])
    );
    line.remaining_active_quantity = Number(data?.remaining_active_quantity ?? line.remaining_active_quantity);
    line.message = data?.allocation_message || "";
    line.lots = (data?.lots || []).map((lot) => normalizeLot(
      lot,
      applyFefo ? recommendations.get(Number(lot.inventory_master_id)) : oldQuantities.get(Number(lot.inventory_master_id))
    ));
  } catch (error) {
    line.message = error?.response?.data?.message || "Available lots could not be loaded.";
    line.lots = [];
  } finally {
    line.loading = false;
  }
}

async function loadAllLots() {
  loadingAllLots.value = true;
  const fixed = cart.value.filter((line) => line.calculation_method !== "balancer");
  const balancers = cart.value.filter((line) => line.calculation_method === "balancer");
  try {
    await Promise.all(fixed.map((line) => loadLots(line, true)));
    await Promise.all(balancers.map((line) => loadLots(line, true)));
  } finally {
    loadingAllLots.value = false;
  }
}

function purityFactor(line, lot) {
  if (line.item_type === "packaging" || line.calculation_method === "balancer") return 1;
  return Number(line.standard_purity || 0) > 0 ? Number(lot.purchase_purity || 0) / 100 : 1;
}

function lotActive(line, lot) {
  return Number(lot.quantity || 0) * purityFactor(line, lot);
}

function linePhysical(line) {
  return line.lots.reduce((total, lot) => total + Number(lot.quantity || 0), 0);
}

function lineActive(line) {
  return line.lots.reduce((total, lot) => total + lotActive(line, lot), 0);
}

function lineAmount(line) {
  return line.lots.reduce((total, lot) => total + Number(lot.quantity || 0) * Number(lot.unit_cost || 0), 0);
}

function allocationClass(line) {
  if (line.calculation_method === "balancer" && lineActive(line) === 0) return "text-warning";
  return lineActive(line) + 0.0001 >= Number(line.remaining_active_quantity || 0)
    ? "text-success"
    : "text-error";
}

function clampLot(lot) {
  const value = Math.max(0, Number(lot.quantity || 0));
  lot.quantity = Math.min(value, Number(lot.available_quantity || 0));
}

function onLotQuantityChange(line, lot) {
  clampLot(lot);
  if (line.calculation_method === "balancer" || line.item_type === "packaging") return;

  // Any change to a fixed ingredient changes the remaining batch weight.
  // Clear stale Salt/PM-free balancer quantities immediately, then recompute
  // with current fixed-lot physical amounts using the backend preview.
  const relatedBalancers = cart.value.filter((candidate) =>
    candidate.calculation_method === "balancer"
    && candidate.production_requisition_detail_id === line.production_requisition_detail_id
  );
  for (const balancer of relatedBalancers) {
    balancer.lots.forEach((row) => { row.quantity = 0; });
    balancer.message = "Recalculating from the selected fixed-material lots...";
  }
  if (balancerRefreshTimer) clearTimeout(balancerRefreshTimer);
  balancerRefreshTimer = setTimeout(() => {
    if (canLoadLots.value && !loadingAllLots.value) {
      Promise.all(relatedBalancers.map((balancer) => loadLots(balancer, true)));
    }
  }, 400);
}

function clearLine(line) {
  line.lots.forEach((lot) => { lot.quantity = 0; });
  if (line.calculation_method !== "balancer" && line.item_type !== "packaging") {
    const balancers = cart.value.filter((candidate) => candidate.calculation_method === "balancer"
      && candidate.production_requisition_detail_id === line.production_requisition_detail_id);
    balancers.forEach((balancer) => {
      balancer.lots.forEach((lot) => { lot.quantity = 0; });
      balancer.message = "Complete fixed-material allocation to refresh Salt FEFO.";
    });
  }
}

function removeLine(line) {
  const index = cart.value.indexOf(line);
  if (index < 0) return;
  cart.value.splice(index, 1);
  const group = line.item_type === "packaging" ? "packaging" : "raw_material";
  openPanels[group] = openPanels[group].filter((id) => id !== line.requisition_raw_detail_id);
}

function validate() {
  if (!form.requisition_master_id || !form.warehouse_id || !form.requisition_delivery_date) {
    return "Requisition, issue warehouse and issue date are required.";
  }
  if (cart.value.length === 0) return "Add at least one material requirement.";

  for (const line of cart.value) {
    for (const lot of line.lots) {
      if (Number(lot.quantity || 0) > Number(lot.available_quantity || 0) + 0.0001) {
        return `${line.product_name}: lot ${lot.lot_no || lot.batch_no} exceeds available stock.`;
      }
    }
    if (line.calculation_method !== "balancer" && line.lots.length > 0 && lineActive(line) <= 0) {
      return `${line.product_name}: select at least one lot or refresh FEFO allocation.`;
    }
  }
  return null;
}

async function submit(status) {
  const message = validate();
  if (message) {
    toast.warning(message);
    return;
  }

  const payload = {
    requisition_delivery_master_id: editId,
    requisition_master_id: form.requisition_master_id,
    warehouse_id: form.warehouse_id,
    department_id: form.department_id || null,
    requisition_delivery_date: formattedDate(form.requisition_delivery_date),
    status,
    remarks: form.remarks || null,
    cart: cart.value.map((line) => {
      const allocations = line.lots
        .filter((lot) => Number(lot.quantity || 0) > 0)
        .map((lot) => ({
          inventory_master_id: lot.inventory_master_id,
          quantity: Number(lot.quantity),
        }));
      const item = {
        requisition_raw_detail_id: line.requisition_raw_detail_id,
        product_id: line.product_id,
        template_id: line.template_id,
      };
      if (allocations.length) item.allocations = allocations;
      return item;
    }),
  };

  saving.value = true;
  try {
    const response = await store.dispatch("saveUpdateInvProductionRequisitionDelivery", payload);
    toast.success(response?.data?.message || "Production material issue saved.");
    router.push({ name: "production-requisition-delivery-records" });
  } catch (error) {
    const errors = error?.response?.data?.errors;
    const text = errors ? Object.values(errors).flat().join("\n") : error?.response?.data?.message;
    toast.error(text || "Production material issue could not be saved.");
  } finally {
    saving.value = false;
  }
}

async function loadEditData(id) {
  try {
    const data = await store.dispatch("getInvProductionRequisitionDeliveryById", id);
    if (data.posted_at || data.approval_current_process || data.approval_processes?.length) {
      toast.warning("Submitted or posted material issues are read-only.");
    }
    if (data.requisition_master && !requisitions.value.some((item) => item.id === data.requisition_master.id)) {
      store.commit("SET_PRODUCTION_RAW_MATERIAL_REQUISITION_LIST", [data.requisition_master, ...requisitions.value]);
    }
    form.requisition_master_id = data.requisition_master_id;
    form.warehouse_id = data.warehouse_id;
    form.department_id = data.department_id;
    form.requisition_delivery_date = data.requisition_delivery_date ? new Date(data.requisition_delivery_date) : new Date();
    form.remarks = data.remarks || "";
    cart.value = (data.requisition_delivery_details || []).map((detail) => {
      const requirement = detail.requisition_detail || {};
      const line = {
        requisition_raw_detail_id: detail.requisition_raw_details_id,
        production_requisition_detail_id: detail.requisition_details_id,
        product_id: detail.product_id,
        template_id: detail.template_id || null,
        product_code: detail.product?.code || "",
        product_name: detail.product?.name || "",
        unit_name: detail.product?.unit?.name || "",
        item_type: requirement.item_type || "raw_material",
        calculation_method: requirement.calculation_method || "fixed",
        standard_purity: Number(detail.standard_purity || requirement.standard_purity || 0),
        total_required_quantity: Number(requirement.total_required_quantity || detail.qty || 0),
        active_required_quantity: Number(detail.required_active_quantity || 0),
        issued_active_quantity: 0,
        remaining_active_quantity: Number(detail.required_active_quantity || detail.issued_active_quantity || 0),
        lots: [],
        loading: false,
        message: "Saved draft allocation. Refresh only if you need current stock suggestions.",
      };
      line.lots = (detail.allocations || []).map((allocation) => normalizeLot({
        ...allocation.inventory_master,
        inventory_master_id: allocation.inventory_master_id,
        lot_no: allocation.lot_no,
        batch_no: allocation.batch_no,
        purchase_purity: allocation.purchase_purity,
        standard_purity: allocation.standard_purity,
        available_quantity: Number(allocation.quantity || 0),
        available_active_quantity: Number(allocation.active_quantity || 0),
        unit_cost: allocation.unit_cost,
      }, allocation.quantity));
      return line;
    });
    expandMaterialGroups();
  } catch (error) {
    toast.error(error?.response?.data?.message || "Material issue draft could not be loaded.");
  }
}

onMounted(async () => {
  await Promise.all([
    store.dispatch("getWarehouseList"),
    store.dispatch("getHrmDepartmentList"),
    store.dispatch("getRawMetarialRequisitionList"),
  ]);
  if (editId) await loadEditData(editId);
});
</script>

<style scoped>
.material-group {
  border: 1px solid rgba(0, 0, 0, 0.14);
  border-radius: 8px;
  overflow: hidden;
}

.material-group-header {
  border-bottom: 1px solid rgba(0, 0, 0, 0.1);
  padding-inline: 20px !important;
}

.material-group--raw .material-group-header {
  background-color: #eff8f5;
}

.material-group--packaging .material-group-header {
  background-color: #f2f3fc;
}

.material-group-panels {
  border-radius: 0;
  min-width: 0;
}

/* Project-wide custom.css removes Vuetify's panel text padding using !important.
   Restore it only on RM / PM delivery panels; don't change other ERP pages. */
.material-group-panels :deep(.v-expansion-panel-title) {
  padding: 12px 20px !important;
}

.material-group-panels :deep(.v-expansion-panel-text__wrapper) {
  padding: 12px 20px 20px !important;
}

/* Keep the lot table inside the panel padding with its own horizontal scroll. */
.material-group-panels :deep(.lot-table) {
  width: 100%;
  max-width: 100%;
  min-width: 0;
}

.material-group-panels :deep(.lot-table .v-table__wrapper) {
  max-width: 100%;
  overflow-x: auto;
}

@media (max-width: 959px) {
  .material-group-header {
    padding-inline: 14px !important;
  }

  .material-group-panels :deep(.v-expansion-panel-title) {
    padding: 12px 14px !important;
  }

  .material-group-panels :deep(.v-expansion-panel-text__wrapper) {
    padding: 12px 14px 16px !important;
  }
}

.metric-label {
  color: rgba(0, 0, 0, 0.6);
  font-size: 0.72rem;
  text-transform: uppercase;
}

.metric-value {
  font-size: 0.9rem;
  font-weight: 600;
}

.lot-table {
  border: 1px solid rgba(0, 0, 0, 0.12);
}

.lot-table th {
  white-space: nowrap;
}
</style>
